import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  AlertOctagon, RefreshCw, Loader2, Search, FilterX,
  ChevronLeft, ChevronRight, Trash2, X, Copy, AlertTriangle, Clock,
} from "lucide-react";

const PAGE_SIZE = 50;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

function StatusPill({ code }) {
  let bg = "#F3F4F6", fg = "#374151";
  if (code >= 500) { bg = "#FEF2F2"; fg = "#991B1B"; }
  else if (code >= 400) { bg = "#FFF7ED"; fg = "#9A3412"; }
  return (
    <span
      data-testid={`error-status-${code}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-bold"
      style={{ background: bg, color: fg }}
    >
      {code}
    </span>
  );
}

function MethodPill({ method }) {
  const colors = {
    GET: { bg: "#EFF6FF", fg: "#1E40AF" },
    POST: { bg: "#ECFDF5", fg: "#065F46" },
    PUT: { bg: "#FFF7ED", fg: "#9A3412" },
    PATCH: { bg: "#F3E8FF", fg: "#6B21A8" },
    DELETE: { bg: "#FEF2F2", fg: "#991B1B" },
  };
  const c = colors[method] || { bg: "#F3F4F6", fg: "#374151" };
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono font-bold"
      style={{ background: c.bg, color: c.fg }}>
      {method}
    </span>
  );
}

function ErrorDetailModal({ error, onClose }) {
  if (!error) return null;
  const copyTraceback = () => {
    navigator.clipboard.writeText(error.traceback || "");
    toast.success("Stacktrace copiato");
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="error-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg" style={{ background: "#FEF2F2" }}>
              <AlertTriangle className="w-5 h-5" style={{ color: "#991B1B" }} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <StatusPill code={error.status_code} />
                <MethodPill method={error.method} />
                <span className="font-mono text-sm font-semibold">{error.path}</span>
              </div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                {fmtDate(error.timestamp)} · {error.error_type}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5" data-testid="btn-close-error-detail">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          {error.error_message && (
            <section>
              <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Messaggio</div>
              <div className="text-sm font-mono p-3 rounded-lg bg-red-50 text-red-900 whitespace-pre-wrap break-words">
                {error.error_message}
              </div>
            </section>
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Utente</div>
              <div className="font-medium">{error.user_email || "anonimo"}</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>{error.user_role || "—"}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Client IP</div>
              <div className="font-mono text-xs">{error.client_ip || "—"}</div>
            </div>
            {error.query && (
              <div className="col-span-2">
                <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Query</div>
                <div className="font-mono text-xs p-2 rounded bg-gray-50 break-all">?{error.query}</div>
              </div>
            )}
            {error.user_agent && (
              <div className="col-span-2">
                <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>User-Agent</div>
                <div className="font-mono text-xs p-2 rounded bg-gray-50 break-all">{error.user_agent}</div>
              </div>
            )}
          </div>
          {error.traceback && (
            <section>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Stacktrace</div>
                <button onClick={copyTraceback} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-black/5"
                  style={{ borderColor: "var(--border)" }} data-testid="btn-copy-traceback">
                  <Copy className="w-3 h-3" /> Copia
                </button>
              </div>
              <pre className="text-xs font-mono p-3 rounded-lg bg-gray-900 text-gray-100 overflow-x-auto whitespace-pre">
{error.traceback}
              </pre>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminErrors() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/admin/errors/stats");
      setStats(r.data);
    } catch { /* silent */ }
  }, []);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = { skip: opts.skip ?? skip, limit: PAGE_SIZE };
      if (statusFilter) params.status_code = statusFilter;
      if (methodFilter) params.method = methodFilter;
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) {
        const d = new Date(dateTo);
        d.setHours(23, 59, 59, 999);
        params.date_to = d.toISOString();
      }
      const r = await api.get("/admin/errors", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSkip(r.data.skip ?? 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
    loadStats();
  }, [skip, statusFilter, methodFilter, q, dateFrom, dateTo, loadStats]);

  useEffect(() => { load({ skip: 0 }); loadStats(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load({ skip: 0 }); /* eslint-disable-next-line */ }, [statusFilter, methodFilter]);

  const openDetail = async (eid) => {
    try {
      const r = await api.get(`/admin/errors/${eid}`);
      setSelected(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (eid) => {
    if (!window.confirm("Eliminare questa voce dal log errori?")) return;
    try {
      await api.delete(`/admin/errors/${eid}`);
      toast.success("Errore eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm("Cancellare TUTTE le voci più vecchie di 30 giorni? L'azione è irreversibile.")) return;
    setCleaning(true);
    try {
      const r = await api.delete("/admin/errors/cleanup", { params: { older_than_days: 30 } });
      toast.success(`${r.data.deleted} errori cancellati`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCleaning(false);
    }
  };

  const resetFilters = () => {
    setStatusFilter(""); setMethodFilter(""); setQ(""); setDateFrom(""); setDateTo(""); setSkip(0);
  };

  const onSearch = (e) => { e.preventDefault(); load({ skip: 0 }); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6 p-6 max-w-[1400px] mx-auto" data-testid="page-admin-errors">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl" style={{ background: "rgba(220,38,38,0.08)" }}>
            <AlertOctagon className="w-6 h-6" style={{ color: "#DC2626" }} />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
              Errori 5xx · Dashboard
            </h1>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Cronologia errori server con stacktrace · ultime 24h: <b>{stats?.last_24h ?? "—"}</b>, ultimi 7g: <b>{stats?.last_7d ?? "—"}</b>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium hover:bg-black/5"
            style={{ borderColor: "var(--border)" }}
            data-testid="btn-refresh-errors"
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Aggiorna
          </button>
          <button
            onClick={handleCleanup}
            disabled={cleaning || !stats?.total}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border disabled:opacity-50"
            style={{ borderColor: "var(--border)" }}
            data-testid="btn-cleanup-errors"
          >
            {cleaning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Cleanup &gt; 30gg
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border p-4 bg-white" style={{ borderColor: "var(--border)" }} data-testid="stat-card-total">
          <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "var(--text-muted)" }}>Totale</div>
          <div className="text-2xl font-extrabold mt-1" style={{ fontFamily: "Manrope" }}>{stats?.total ?? "—"}</div>
        </div>
        <div className="rounded-xl border p-4 bg-white" style={{ borderColor: "var(--border)" }} data-testid="stat-card-24h">
          <div className="text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1" style={{ color: "#991B1B" }}>
            <Clock className="w-3 h-3" /> Ultime 24h
          </div>
          <div className="text-2xl font-extrabold mt-1" style={{ color: "#991B1B", fontFamily: "Manrope" }}>{stats?.last_24h ?? "—"}</div>
        </div>
        <div className="rounded-xl border p-4 bg-white" style={{ borderColor: "var(--border)" }} data-testid="stat-card-7d">
          <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "var(--text-muted)" }}>Ultimi 7 giorni</div>
          <div className="text-2xl font-extrabold mt-1" style={{ fontFamily: "Manrope" }}>{stats?.last_7d ?? "—"}</div>
        </div>
        <div className="rounded-xl border p-4 bg-white" style={{ borderColor: "var(--border)" }} data-testid="stat-card-top">
          <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "var(--text-muted)" }}>Top endpoint</div>
          <div className="text-sm font-mono font-semibold mt-1 truncate" title={stats?.top_paths?.[0]?.path}>
            {stats?.top_paths?.[0]?.path || "—"}
          </div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>{stats?.top_paths?.[0]?.count || 0} occorrenze</div>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2 p-3 rounded-xl border" style={{ borderColor: "var(--border)", background: "white" }}>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per messaggio, path, stacktrace…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "var(--border)" }}
            data-testid="filter-errors-q"
          />
        </div>
        <select
          value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="filter-errors-method"
        >
          <option value="">Tutti i metodi</option>
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
          <option value="DELETE">DELETE</option>
        </select>
        <select
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="filter-errors-status"
        >
          <option value="">Tutti gli status</option>
          <option value="500">500</option>
          <option value="502">502</option>
          <option value="503">503</option>
          <option value="504">504</option>
        </select>
        <input
          type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--border)" }}
          data-testid="filter-errors-from"
        />
        <input
          type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--border)" }}
          data-testid="filter-errors-to"
        />
        <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ background: "var(--brand-green)" }} data-testid="btn-apply-error-filters">
          Cerca
        </button>
        {(statusFilter || methodFilter || q || dateFrom || dateTo) && (
          <button type="button" onClick={resetFilters} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--border)" }} data-testid="btn-reset-error-filters">
            <FilterX className="w-4 h-4" /> Reset
          </button>
        )}
      </form>

      {/* Table */}
      <div className="rounded-xl border overflow-hidden bg-white" style={{ borderColor: "var(--border)" }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Quando</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Metodo</th>
                <th className="px-4 py-3 font-semibold">Path</th>
                <th className="px-4 py-3 font-semibold">Errore</th>
                <th className="px-4 py-3 font-semibold">Utente</th>
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline-block" /></td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Nessun errore registrato. Tutto ok!</td></tr>
              )}
              {items.map((e) => (
                <tr key={e.id} className="border-t hover:bg-gray-50/60 cursor-pointer" style={{ borderColor: "var(--border)" }}
                  onClick={() => openDetail(e.id)}
                  data-testid={`error-row-${e.id}`}>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(e.timestamp)}</td>
                  <td className="px-4 py-3"><StatusPill code={e.status_code} /></td>
                  <td className="px-4 py-3"><MethodPill method={e.method} /></td>
                  <td className="px-4 py-3 font-mono text-xs max-w-[260px]"><span className="block truncate" title={e.path}>{e.path}</span></td>
                  <td className="px-4 py-3 max-w-[360px]">
                    <div className="font-semibold text-xs text-red-700">{e.error_type}</div>
                    <div className="block truncate text-xs text-gray-700" title={e.error_message}>{e.error_message || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{e.user_email || "anonimo"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(ev) => { ev.stopPropagation(); handleDelete(e.id); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium hover:bg-black/5"
                      style={{ borderColor: "var(--border)" }}
                      data-testid={`btn-delete-error-${e.id}`}
                      title="Elimina"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            {total === 0 ? "Nessun risultato" : `${skip + 1}–${Math.min(skip + items.length, total)} di ${total}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={skip <= 0 || loading}
              onClick={() => { const ns = Math.max(0, skip - PAGE_SIZE); setSkip(ns); load({ skip: ns }); }}
              className="p-1.5 rounded-md border disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
              data-testid="btn-errors-page-prev"
            ><ChevronLeft className="w-4 h-4" /></button>
            <div className="text-xs font-medium">Pagina {currentPage} / {totalPages}</div>
            <button
              disabled={skip + items.length >= total || loading}
              onClick={() => { const ns = skip + PAGE_SIZE; setSkip(ns); load({ skip: ns }); }}
              className="p-1.5 rounded-md border disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
              data-testid="btn-errors-page-next"
            ><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      <ErrorDetailModal error={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
